/**
 * @file GridListToggle.tsx
 *
 * Two-state toggle that switches the product list between grid and list views.
 * Renders a Gravity-styled toggle bar with an icon + label per mode.
 */

import { Icon } from '@group.one/react-gravity';
import { FormattedMessage } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import type { ViewMode } from './product-list';
import styles from './GridListToggle.module.css';

interface GridListToggleProps {
  viewMode: ViewMode;
  setViewMode: (viewMode: ViewMode) => void;
}

/**
 * Renders the grid/list view toggle.
 *
 * @param props.viewMode - The active view mode.
 * @param props.setViewMode - Switches the active view mode.
 *
 * @returns The toggle control.
 */
export function GridListToggle({ viewMode, setViewMode }: GridListToggleProps) {
  return (
    <div className={`${styles.bar} gv-toggle gv-surface-bright gv-flex gv-border gv-radius`}>
      <button
        type="button"
        data-testid="products-view-grid"
        aria-pressed={viewMode === 'grid'}
        className={`${styles.toggleButton} gv-button-toggle ${viewMode === 'grid' ? styles.active : ''} gv-p-xs gv-flex gv-gap-xs gv-items-center gv-radius-l`}
        onClick={() => setViewMode('grid')}
      >
        <Icon name="grid_view" size="small" />
        <span className="gv-text-sm gv-pr-xs">
          <FormattedMessage {...adminMessages.productsViewGrid} />
        </span>
      </button>
      <button
        type="button"
        data-testid="products-view-list"
        aria-pressed={viewMode === 'list'}
        className={`${styles.toggleButton} gv-button-toggle ${viewMode === 'list' ? styles.active : ''} gv-p-xs gv-flex gv-gap-xs gv-items-center gv-radius-r`}
        onClick={() => setViewMode('list')}
      >
        <Icon name="view_headline" size="small" />
        <span className="gv-text-sm gv-pr-xs">
          <FormattedMessage {...adminMessages.productsViewList} />
        </span>
      </button>
    </div>
  );
}
